import React from 'react';
import { ToastContainer, Bounce, toast } from 'react-toastify';
import 'react-toastify/dist/ReactToastify.css';
import './App.css';
import Header from './components/Header/Header';
import NodeManager from './components/NodeManager/NodeManager';
import Canvas from './components/Canvas/Canvas';
//import NodeInspector from './components/NodeInspector/NodeInspector';
import DataInspector from './components/DataInspector/DataInspector';
import ScenarioAnalysis from './components/ScenarioAnalysis/ScenarioAnalysis';
import MultiUnitOptimization from './components/MultiUnitOptimization/MultiUnitOptimization';
import { usePipeline} from './context/PipelineContext';
import { DnDProvider } from './context/DnDContext'; // Import the DnDProvider
import { ReactFlowProvider } from 'reactflow';
import {
  clearActivePipelineContext,
  completePipelineMigration,
  copyMigrationPipelines,
  approveAdminAccount,
  getAdminUsageUsers,
  getCurrentUser,
  listAdminAccounts,
  listMigrationPipelines,
  login as loginApi,
  rejectAdminAccount,
  signup as signupApi,
} from './api/api';

const USAGE_EVENT_TYPES = [
  'pipeline_created',
  'pipeline_opened',
  'pipeline_updated',
  'pipeline_executed',
  'pipeline_deleted',
  'artifact_downloaded',
];

const eventLabel = (eventType) => (
  eventType
    .replace('pipeline_', '')
    .replace('artifact_', '')
    .replaceAll('_', ' ')
);

const brandOrbitDots = [
  { x: 8, y: 28, size: 10, color: 'yellow' },
  { x: 22, y: 8, size: 10, color: 'yellow' },
  { x: 46, y: 2, size: 12, color: 'blue' },
  { x: 70, y: 9, size: 10, color: 'yellow' },
  { x: 88, y: 31, size: 9, color: 'blue' },
  { x: 4, y: 55, size: 8, color: 'blue' },
  { x: 18, y: 75, size: 10, color: 'yellow' },
  { x: 47, y: 88, size: 11, color: 'yellow' },
  { x: 76, y: 76, size: 9, color: 'blue' },
  { x: 93, y: 56, size: 8, color: 'yellow' },
  { x: 30, y: 34, size: 7, color: 'blue' },
  { x: 62, y: 35, size: 7, color: 'yellow' },
];

const IntegrumBrandLockup = () => (
  <div className="auth-brand-lockup" aria-label="Integrum Energy">
    <div className="integrum-emblem" aria-hidden="true">
      {brandOrbitDots.map((dot, index) => (
        <span
          key={`${dot.x}-${dot.y}-${index}`}
          className={`integrum-dot integrum-dot-${dot.color}`}
          style={{
            '--dot-x': `${dot.x}px`,
            '--dot-y': `${dot.y}px`,
            '--dot-size': `${dot.size}px`,
          }}
        />
      ))}
      <span className="integrum-sun">
        <span className="integrum-turbine-mast" />
        <span className="integrum-turbine-blade integrum-turbine-blade-top" />
        <span className="integrum-turbine-blade integrum-turbine-blade-left" />
        <span className="integrum-turbine-blade integrum-turbine-blade-right" />
      </span>
    </div>
    <div className="integrum-wordmark" aria-hidden="true">
      <span className="integrum-wordmark-main">INTEGRUM</span>
      <span className="integrum-wordmark-sub">ENERGY</span>
    </div>
  </div>
);

const AdminOpsPage = ({ onBack }) => {
  const [users, setUsers] = React.useState([]);
  const [accounts, setAccounts] = React.useState([]);
  const [loading, setLoading] = React.useState(true);
  const [error, setError] = React.useState('');
  const [accountsError, setAccountsError] = React.useState('');
  const [accountActionId, setAccountActionId] = React.useState('');

  const loadAccounts = React.useCallback(async () => {
    setAccountsError('');
    try {
      const data = await listAdminAccounts('pending');
      setAccounts(data?.accounts || []);
    } catch (err) {
      setAccountsError(err.message || 'Could not load accounts waiting for approval');
    }
  }, []);

  const loadUsage = React.useCallback(async () => {
    setLoading(true);
    setError('');
    try {
      const data = await getAdminUsageUsers();
      setUsers(data?.users || []);
    } catch (err) {
      setError(err.message || 'Could not load admin usage data');
    } finally {
      setLoading(false);
    }
  }, []);

  const decideAccount = async (userId, decision) => {
    setAccountActionId(userId);
    setAccountsError('');
    try {
      if (decision === 'approve') {
        await approveAdminAccount(userId);
      } else {
        await rejectAdminAccount(userId);
      }
      await loadAccounts();
    } catch (err) {
      setAccountsError(err.message || 'Could not update that account');
    } finally {
      setAccountActionId('');
    }
  };

  React.useEffect(() => {
    loadUsage();
    loadAccounts();
  }, [loadUsage, loadAccounts]);

  const totals = users.reduce((acc, user) => {
    const events = user.events || {};
    USAGE_EVENT_TYPES.forEach((eventType) => {
      acc[eventType] = (acc[eventType] || 0) + Number(events[eventType]?.count || 0);
    });
    acc.total += Object.values(events).reduce((sum, event) => sum + Number(event?.count || 0), 0);
    return acc;
  }, { total: 0 });

  const activeUsers = users.filter((user) => Object.keys(user.events || {}).length > 0).length;

  return (
    <main style={{
      minHeight: 'calc(100vh - 56px)',
      background: '#f8fafc',
      padding: '32px',
      color: '#0f172a',
      overflowY: 'auto'
    }}>
      <section style={{ maxWidth: '1280px', margin: '0 auto' }}>
        <div style={{ display: 'flex', justifyContent: 'space-between', gap: '24px', alignItems: 'flex-start', marginBottom: '28px' }}>
          <div>
            <p style={{ margin: '0 0 8px', color: '#0891b2', fontSize: '12px', fontWeight: 800, letterSpacing: '0.14em', textTransform: 'uppercase' }}>
              Admin Ops
            </p>
            <h1 style={{ margin: 0, fontSize: '34px', letterSpacing: '-0.04em' }}>Usage & Tracking Dashboard</h1>
            <p style={{ margin: '10px 0 0', color: '#64748b', fontSize: '15px' }}>
              Monitor account activity, pipeline usage, downloads, and recent daily activity across all users.
            </p>
          </div>
          <div style={{ display: 'flex', gap: '12px' }}>
            <button onClick={onBack} style={{ border: '1px solid #cbd5e1', background: '#ffffff', color: '#0f172a', borderRadius: '999px', padding: '11px 16px', fontWeight: 750, cursor: 'pointer' }}>
              Back to Workspace
            </button>
            <button onClick={() => { loadUsage(); loadAccounts(); }} disabled={loading} style={{ border: 'none', background: 'linear-gradient(135deg, #22c55e, #0ea5e9)', color: '#082f49', borderRadius: '999px', padding: '11px 16px', fontWeight: 850, cursor: loading ? 'default' : 'pointer' }}>
              {loading ? 'Refreshing...' : 'Refresh'}
            </button>
          </div>
        </div>

        <div style={{ background: '#ffffff', border: '1px solid #e2e8f0', borderRadius: '24px', padding: '22px', boxShadow: '0 18px 42px rgba(15,23,42,0.07)', marginBottom: '24px' }}>
          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '18px' }}>
            <div>
              <h2 style={{ margin: 0, fontSize: '22px', letterSpacing: '-0.03em' }}>Accounts waiting for approval</h2>
              <p style={{ margin: '6px 0 0', color: '#64748b', fontSize: '13px' }}>
                Signups from other domains stay pending until an admin approves them. @integrumenergy.in accounts are active immediately.
              </p>
            </div>
            <span style={{ color: '#64748b', fontSize: '13px' }}>{accounts.length} pending</span>
          </div>
          {accountsError && (
            <div style={{ padding: '14px 16px', borderRadius: '16px', background: '#fef2f2', color: '#991b1b', border: '1px solid #fecaca', marginBottom: '16px' }}>
              {accountsError}
            </div>
          )}
          {accounts.length === 0 ? (
            <div style={{ padding: '18px', textAlign: 'center', color: '#64748b' }}>No accounts are waiting for approval.</div>
          ) : (
            <div style={{ display: 'grid', gap: '12px' }}>
              {accounts.map((account) => (
                <article key={account.user_id} style={{ display: 'flex', justifyContent: 'space-between', gap: '16px', alignItems: 'center', border: '1px solid #e2e8f0', borderRadius: '16px', padding: '14px 16px' }}>
                  <div>
                    <div style={{ fontWeight: 800 }}>{account.email}</div>
                    <div style={{ marginTop: '4px', color: '#64748b', fontSize: '13px' }}>
                      Requested {account.created_at ? new Date(account.created_at).toLocaleString() : 'recently'}
                    </div>
                  </div>
                  <div style={{ display: 'flex', gap: '8px' }}>
                    <button
                      type="button"
                      disabled={accountActionId === account.user_id}
                      onClick={() => decideAccount(account.user_id, 'approve')}
                      style={{ border: 'none', background: '#22c55e', color: '#052e16', borderRadius: '999px', padding: '9px 14px', fontWeight: 800, cursor: 'pointer' }}
                    >
                      Approve
                    </button>
                    <button
                      type="button"
                      disabled={accountActionId === account.user_id}
                      onClick={() => decideAccount(account.user_id, 'reject')}
                      style={{ border: '1px solid #fecaca', background: '#fff', color: '#991b1b', borderRadius: '999px', padding: '9px 14px', fontWeight: 800, cursor: 'pointer' }}
                    >
                      Reject
                    </button>
                  </div>
                </article>
              ))}
            </div>
          )}
        </div>

        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(4, minmax(0, 1fr))', gap: '16px', marginBottom: '24px' }}>
          {[
            ['Total Users', users.length],
            ['Active Users', activeUsers],
            ['Total Events', totals.total],
            ['Pipeline Runs', totals.pipeline_executed || 0],
          ].map(([label, value]) => (
            <div key={label} style={{ background: '#ffffff', border: '1px solid #e2e8f0', borderRadius: '22px', padding: '20px', boxShadow: '0 16px 36px rgba(15,23,42,0.06)' }}>
              <div style={{ color: '#64748b', fontSize: '13px', fontWeight: 750 }}>{label}</div>
              <div style={{ marginTop: '8px', fontSize: '34px', fontWeight: 850, letterSpacing: '-0.04em' }}>{value}</div>
            </div>
          ))}
        </div>

        <div style={{ background: '#ffffff', border: '1px solid #e2e8f0', borderRadius: '24px', padding: '22px', boxShadow: '0 18px 42px rgba(15,23,42,0.07)' }}>
          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '18px' }}>
            <h2 style={{ margin: 0, fontSize: '22px', letterSpacing: '-0.03em' }}>User Activity</h2>
            <span style={{ color: '#64748b', fontSize: '13px' }}>{users.length} accounts</span>
          </div>

          {error && (
            <div style={{ padding: '14px 16px', borderRadius: '16px', background: '#fef2f2', color: '#991b1b', border: '1px solid #fecaca', marginBottom: '16px' }}>
              {error}
            </div>
          )}

          {loading ? (
            <div style={{ padding: '32px', textAlign: 'center', color: '#64748b' }}>Loading usage data...</div>
          ) : users.length === 0 ? (
            <div style={{ padding: '32px', textAlign: 'center', color: '#64748b' }}>No users found.</div>
          ) : (
            <div style={{ display: 'grid', gap: '16px' }}>
              {users.map((user) => {
                const events = user.events || {};
                const userTotal = Object.values(events).reduce((sum, event) => sum + Number(event?.count || 0), 0);
                const latestDaily = (user.daily || [])[0] || {};
                return (
                  <article key={user.user_id} style={{ border: '1px solid #e2e8f0', borderRadius: '20px', padding: '18px', background: 'linear-gradient(180deg, #ffffff 0%, #f8fafc 100%)' }}>
                    <div style={{ display: 'grid', gridTemplateColumns: 'minmax(220px, 1.2fr) minmax(120px, 0.4fr)', gap: '18px', alignItems: 'start', marginBottom: '16px' }}>
                      <div>
                        <div style={{ fontSize: '17px', fontWeight: 850 }}>{user.email}</div>
                        <div style={{ marginTop: '5px', color: '#64748b', fontSize: '13px' }}>
                          Role: <strong>{user.role || 'user'}</strong> · {user.last_login_at ? `Last login ${new Date(user.last_login_at).toLocaleString()}` : 'No login recorded'}
                        </div>
                      </div>
                      <div style={{ justifySelf: 'end', textAlign: 'center', borderRadius: '16px', padding: '10px 16px', background: '#ecfeff', border: '1px solid #bae6fd', color: '#0e7490' }}>
                        <div style={{ fontSize: '26px', fontWeight: 900, lineHeight: 1 }}>{userTotal}</div>
                        <div style={{ fontSize: '11px', fontWeight: 800, textTransform: 'uppercase', letterSpacing: '0.1em' }}>events</div>
                      </div>
                    </div>

                    <div style={{ display: 'grid', gridTemplateColumns: 'repeat(6, minmax(110px, 1fr))', gap: '10px' }}>
                      {USAGE_EVENT_TYPES.map((eventType) => (
                        <div key={eventType} style={{ borderRadius: '16px', background: '#f1f5f9', padding: '12px', minHeight: '64px' }}>
                          <div style={{ color: '#64748b', fontSize: '12px', fontWeight: 750, textTransform: 'capitalize' }}>{eventLabel(eventType)}</div>
                          <div style={{ marginTop: '6px', fontSize: '22px', fontWeight: 850 }}>{events[eventType]?.count || 0}</div>
                        </div>
                      ))}
                    </div>

                    <div style={{ display: 'flex', gap: '16px', marginTop: '14px', color: '#64748b', fontSize: '13px' }}>
                      <span>Latest day: <strong style={{ color: '#0f172a' }}>{latestDaily.date || 'No activity'}</strong></span>
                      <span>Daily events: <strong style={{ color: '#0f172a' }}>{latestDaily.total_events || 0}</strong></span>
                    </div>
                  </article>
                );
              })}
            </div>
          )}
        </div>
      </section>
    </main>
  );
};

const App = () => {
  const { session } = usePipeline(); // Access the session from the context
  const [authToken, setAuthToken] = React.useState(() => localStorage.getItem('pipeline_token'));
  const [email, setEmail] = React.useState('');
  const [password, setPassword] = React.useState('');
  const [confirmPassword, setConfirmPassword] = React.useState('');
  const [authMode, setAuthMode] = React.useState('signup');
  const [isSubmitting, setIsSubmitting] = React.useState(false);
  const [currentUser, setCurrentUser] = React.useState(null);
  const [migrationState, setMigrationState] = React.useState({ loading: false, completed: true, pipelines: [] });
  const [selectedMigrationPipelines, setSelectedMigrationPipelines] = React.useState([]);
  const [activePage, setActivePage] = React.useState('workspace');
  const [signupNotice, setSignupNotice] = React.useState('');

  // Show toast when pipeline is successful but no node is selected
  React.useEffect(() => {
    // Only show the toast when the pipeline has just been run successfully
    // and there's no selected node
    if (session.last_run_status === 'success' && !session.selected_node) {
      toast.info('Select a node to view data input and output at the node', {
        position: "bottom-right",
        autoClose: 5000,
        hideProgressBar: false,
        closeOnClick: true,
        pauseOnHover: true,
        draggable: true,
        toastId: 'select-node-toast' // Prevent duplicate toasts
      });
    }
  }, [session.last_run_status, session.selected_node]);

  const refreshUserAndMigration = React.useCallback(async () => {
    if (!localStorage.getItem('pipeline_token')) return;
    try {
      const me = await getCurrentUser();
      const user = me?.user || null;
      setCurrentUser(user);
      if (user?.pipeline_migration_completed_at) {
        setMigrationState({ loading: false, completed: true, pipelines: [] });
        return;
      }
      setMigrationState((prev) => ({ ...prev, loading: true }));
      const migration = await listMigrationPipelines();
      setMigrationState({
        loading: false,
        completed: Boolean(migration?.completed),
        pipelines: migration?.pipelines || [],
      });
    } catch (error) {
      setMigrationState({ loading: false, completed: true, pipelines: [] });
    }
  }, []);

  React.useEffect(() => {
    if (authToken) {
      refreshUserAndMigration();
    }
  }, [authToken, refreshUserAndMigration]);

  const handleLogin = async (event) => {
    event.preventDefault();
    if (!email || !password) {
      toast.error('Please enter email and password', {
        position: "bottom-right",
        autoClose: 3000,
      });
      return;
    }
    if (authMode === 'signup' && password !== confirmPassword) {
      toast.error('Passwords do not match', {
        position: "bottom-right",
        autoClose: 3000,
      });
      return;
    }
    setIsSubmitting(true);
    try {
      const action = authMode === 'signup' ? signupApi : loginApi;
      const data = await action(email, password);
      if (authMode === 'signup' && data?.status === 'pending') {
        setSignupNotice(data.message || 'Account is waiting for admin approval');
        setPassword('');
        setConfirmPassword('');
        toast.success('Account request submitted. An admin must approve it before you can sign in.', {
          position: "bottom-right",
          autoClose: 4000,
        });
        return;
      }
      if (data && data.access_token) {
        setSignupNotice('');
        localStorage.setItem('pipeline_token', data.access_token);
        setAuthToken(data.access_token);
        toast.success(authMode === 'signup' ? 'Account created successfully' : 'Logged in successfully', {
          position: "bottom-right",
          autoClose: 3000,
        });
      } else {
        throw new Error('Unexpected login response');
      }
    } catch (error) {
      toast.error(error.message || 'Login failed', {
        position: "bottom-right",
        autoClose: 4000,
      });
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleMigrationToggle = (pipelineName) => {
    setSelectedMigrationPipelines((prev) => (
      prev.includes(pipelineName)
        ? prev.filter((name) => name !== pipelineName)
        : [...prev, pipelineName]
    ));
  };

  const handleCopySelectedPipelines = async () => {
    setIsSubmitting(true);
    try {
      await copyMigrationPipelines(selectedMigrationPipelines);
      setSelectedMigrationPipelines([]);
      setMigrationState({ loading: false, completed: true, pipelines: [] });
      toast.success('Selected pipelines copied into your account', { position: "bottom-right", autoClose: 3000 });
    } catch (error) {
      toast.error(error.message || 'Pipeline migration failed', { position: "bottom-right", autoClose: 4000 });
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleSkipMigration = async () => {
    setIsSubmitting(true);
    try {
      await completePipelineMigration();
      setSelectedMigrationPipelines([]);
      setMigrationState({ loading: false, completed: true, pipelines: [] });
    } catch (error) {
      toast.error(error.message || 'Could not complete migration step', { position: "bottom-right", autoClose: 4000 });
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleSignOut = () => {
    try {
      localStorage.removeItem('pipeline_token');
      clearActivePipelineContext();
    } catch (error) {
      // Ignore storage errors and still reset local auth state.
    }
    setAuthToken(null);
    setCurrentUser(null);
    setEmail('');
    setPassword('');
    setConfirmPassword('');
    setAuthMode('signup');
    setSignupNotice('');
    setSelectedMigrationPipelines([]);
    setMigrationState({ loading: false, completed: true, pipelines: [] });
    setActivePage('workspace');
    toast.info('Signed out successfully', {
      position: "bottom-right",
      autoClose: 2500,
    });
  };

  if (!authToken) {
    return (
      <div style={{
        minHeight: '100vh',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        background: 'radial-gradient(circle at top left, #dbeafe 0, transparent 32%), linear-gradient(135deg, #f8fafc 0%, #eef2ff 52%, #ecfeff 100%)',
        padding: '32px'
      }}>
        <ToastContainer
          position="bottom-right"
          autoClose={5000}
          hideProgressBar={false}
          newestOnTop={false}
          closeOnClick={false}
          rtl={false}
          pauseOnFocusLoss
          draggable
          pauseOnHover
          theme="colored"
          transition={Bounce}
        />
        <div className="auth-card">
          <div className="auth-layout">
            <aside className="auth-brand-panel">
              <div className="auth-panel-glow auth-panel-glow-a" />
              <div className="auth-panel-glow auth-panel-glow-b" />
              <div className="auth-brand-content">
                <IntegrumBrandLockup />
                <p className="auth-eyebrow">Renewable analytics workspace</p>
                <h1 className="auth-brand-title">
                  Model the numbers behind every energy asset.
                </h1>
                <p className="auth-brand-copy">
                  Secure access to project pipelines, operating assumptions, and finance-ready outputs in one focused workspace.
                </p>
              </div>
              <div className="auth-brand-footer">
                <div className="auth-signal-card">
                  <span>Live pipeline canvas</span>
                  <strong>From source data to investment view</strong>
                </div>
                <div className="auth-metric-row">
                  <div>
                    <span>Scope</span>
                    <strong>Wind + Solar</strong>
                  </div>
                  <div>
                    <span>Access</span>
                    <strong>Account bound</strong>
                  </div>
                </div>
              </div>
            </aside>

            <section style={{ padding: '42px 46px', display: 'flex', flexDirection: 'column', justifyContent: 'center' }}>
              <div style={{ marginBottom: '26px' }}>
                <p style={{
                  margin: 0,
                  marginBottom: '10px',
                  color: '#0891b2',
                  fontSize: '12px',
                  fontWeight: 700,
                  letterSpacing: '0.12em',
                  textTransform: 'uppercase'
                }}>
                  {authMode === 'signup' ? 'Create workspace' : 'Welcome back'}
                </p>
                <h2 style={{
                  margin: 0,
                  marginBottom: '10px',
                  fontSize: '30px',
                  fontWeight: 700,
                  letterSpacing: '-0.03em'
                }}>
                  {authMode === 'signup' ? 'Create your account' : 'Sign in to your account'}
                </h2>
                <p style={{ margin: 0, fontSize: '14px', color: '#64748b', lineHeight: 1.6 }}>
                  {authMode === 'signup'
                    ? '@integrumenergy.in accounts can sign in immediately. Other email domains are submitted for admin approval.'
                    : 'Use the email and password linked to your workspace.'}
                </p>
              </div>

              {signupNotice && (
                <div style={{ marginBottom: '16px', padding: '12px 14px', borderRadius: '14px', background: '#ecfdf5', color: '#065f46', border: '1px solid #a7f3d0', fontSize: '14px', lineHeight: 1.5 }}>
                  {signupNotice} You can sign in after an admin approves the account.
                </div>
              )}
              <form onSubmit={handleLogin}>
                <div style={{ marginBottom: '16px' }}>
                  <label style={{ display: 'block', marginBottom: '8px', fontSize: '13px', fontWeight: 700, color: '#334155' }}>
                    Email
                  </label>
                  <input
                    type="email"
                    value={email}
                    autoComplete="email"
                    placeholder="name@company.com"
                    onChange={(e) => setEmail(e.target.value)}
                    style={{
                      width: '100%',
                      boxSizing: 'border-box',
                      padding: '13px 14px',
                      borderRadius: '14px',
                      border: '1px solid #cbd5e1',
                      background: '#f8fafc',
                      color: '#0f172a',
                      fontSize: '15px',
                      outline: 'none'
                    }}
                  />
                </div>
                <div style={{ marginBottom: authMode === 'signup' ? '16px' : '22px' }}>
                  <label style={{ display: 'block', marginBottom: '8px', fontSize: '13px', fontWeight: 700, color: '#334155' }}>
                    Password
                  </label>
                  <input
                    type="password"
                    value={password}
                    autoComplete={authMode === 'signup' ? 'new-password' : 'current-password'}
                    placeholder="At least 8 characters"
                    onChange={(e) => setPassword(e.target.value)}
                    style={{
                      width: '100%',
                      boxSizing: 'border-box',
                      padding: '13px 14px',
                      borderRadius: '14px',
                      border: '1px solid #cbd5e1',
                      background: '#f8fafc',
                      color: '#0f172a',
                      fontSize: '15px',
                      outline: 'none'
                    }}
                  />
                </div>
                {authMode === 'signup' && (
                  <div style={{ marginBottom: '22px' }}>
                    <label style={{ display: 'block', marginBottom: '8px', fontSize: '13px', fontWeight: 700, color: '#334155' }}>
                      Confirm Password
                    </label>
                    <input
                      type="password"
                      value={confirmPassword}
                      autoComplete="new-password"
                      placeholder="Re-enter your password"
                      onChange={(e) => setConfirmPassword(e.target.value)}
                      style={{
                        width: '100%',
                        boxSizing: 'border-box',
                        padding: '13px 14px',
                        borderRadius: '14px',
                        border: confirmPassword && password !== confirmPassword ? '1px solid #ef4444' : '1px solid #cbd5e1',
                        background: '#f8fafc',
                        color: '#0f172a',
                        fontSize: '15px',
                        outline: 'none'
                      }}
                    />
                    {confirmPassword && password !== confirmPassword && (
                      <p style={{ margin: '8px 0 0', color: '#dc2626', fontSize: '12px' }}>Passwords do not match.</p>
                    )}
                  </div>
                )}
                <button
                  type="submit"
                  disabled={isSubmitting}
                  style={{
                    width: '100%',
                    padding: '13px 16px',
                    borderRadius: '999px',
                    border: 'none',
                    background: isSubmitting ? '#94a3b8' : 'linear-gradient(135deg, #22c55e, #0ea5e9)',
                    color: '#082f49',
                    fontWeight: 800,
                    fontSize: '15px',
                    cursor: isSubmitting ? 'default' : 'pointer',
                    boxShadow: '0 18px 40px rgba(14,165,233,0.24)',
                    transition: 'transform 0.1s ease, box-shadow 0.1s ease'
                  }}
                >
                  {isSubmitting ? 'Submitting...' : (authMode === 'signup' ? 'Create Account' : 'Sign In')}
                </button>
              </form>
              <button
                type="button"
                onClick={() => {
                  setAuthMode(authMode === 'signup' ? 'login' : 'signup');
                  setSignupNotice('');
                  setPassword('');
                  setConfirmPassword('');
                }}
                style={{
                  marginTop: '18px',
                  width: '100%',
                  border: 'none',
                  background: 'transparent',
                  color: '#0369a1',
                  cursor: 'pointer',
                  fontSize: '14px',
                  fontWeight: 600
                }}
              >
                {authMode === 'signup' ? 'Already have an account? Sign in' : 'Need an account? Create one'}
              </button>
            </section>
          </div>
        </div>
      </div>
    );
  }

  if (!migrationState.completed) {
    return (
      <div style={{
        minHeight: '100vh',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        background: '#f3f4f6',
        padding: '24px'
      }}>
        <ToastContainer position="bottom-right" autoClose={5000} hideProgressBar={false} newestOnTop={false} closeOnClick={false} rtl={false} pauseOnFocusLoss draggable pauseOnHover theme="colored" transition={Bounce} />
        <div style={{
          background: '#ffffff',
          borderRadius: '16px',
          padding: '32px 40px',
          boxShadow: '0 24px 60px rgba(148,163,184,0.45)',
          border: '1px solid rgba(209,213,219,0.9)',
          width: '100%',
          maxWidth: '560px',
          color: '#111827'
        }}>
          <h2 style={{ margin: 0, marginBottom: '8px', fontSize: '24px', fontWeight: 600 }}>
            Import Existing Pipelines
          </h2>
          <p style={{ margin: 0, marginBottom: '20px', fontSize: '13px', color: '#6b7280' }}>
            Select any admin-owned legacy pipelines you want copied into {currentUser?.email || 'your account'}.
            Run history and artifacts stay with the admin account.
          </p>
          {migrationState.loading ? (
            <p>Loading available pipelines...</p>
          ) : migrationState.pipelines.length === 0 ? (
            <p>No legacy pipelines are available to import.</p>
          ) : (
            <div style={{ maxHeight: '280px', overflowY: 'auto', marginBottom: '20px' }}>
              {migrationState.pipelines.map((pipeline) => (
                <label
                  key={pipeline.pipeline_name}
                  style={{
                    display: 'flex',
                    alignItems: 'center',
                    gap: '10px',
                    padding: '10px 0',
                    borderBottom: '1px solid #e5e7eb',
                    fontSize: '14px'
                  }}
                >
                  <input
                    type="checkbox"
                    checked={selectedMigrationPipelines.includes(pipeline.pipeline_name)}
                    onChange={() => handleMigrationToggle(pipeline.pipeline_name)}
                  />
                  <span>{pipeline.pipeline_name}</span>
                </label>
              ))}
            </div>
          )}
          <div style={{ display: 'flex', gap: '12px', justifyContent: 'flex-end' }}>
            <button type="button" onClick={handleSkipMigration} disabled={isSubmitting} style={{ padding: '10px 14px', borderRadius: '999px', border: '1px solid #d1d5db', background: '#ffffff', cursor: 'pointer' }}>
              Skip
            </button>
            <button
              type="button"
              onClick={handleCopySelectedPipelines}
              disabled={isSubmitting || selectedMigrationPipelines.length === 0}
              style={{ padding: '10px 14px', borderRadius: '999px', border: 'none', background: selectedMigrationPipelines.length === 0 ? '#9ca3af' : 'linear-gradient(135deg, #22c55e, #0ea5e9)', color: '#0b1120', fontWeight: 600, cursor: selectedMigrationPipelines.length === 0 ? 'default' : 'pointer' }}
            >
              Copy Selected
            </button>
          </div>
        </div>
      </div>
    );
  }

  return (
      <DnDProvider>
      <Header
        currentUser={currentUser}
        onSignOut={handleSignOut}
        onOpenAdminOps={() => setActivePage('adminOps')}
        onOpenScenarioAnalysis={() => setActivePage('scenarioAnalysis')}
        onOpenMultiUnitOptimization={() => setActivePage('multiUnitOptimization')}
        onOpenWorkspace={() => setActivePage('workspace')}
        activePage={activePage}
      />
      {activePage === 'adminOps' && currentUser?.role === 'admin' ? (
        <AdminOpsPage onBack={() => setActivePage('workspace')} />
      ) : activePage === 'scenarioAnalysis' ? (
        <ScenarioAnalysis onClose={() => setActivePage('workspace')} />
      ) : activePage === 'multiUnitOptimization' ? (
        <MultiUnitOptimization onClose={() => setActivePage('workspace')} />
      ) : (
      <div style={{ display: 'flex' }}>
      <ToastContainer
        position="bottom-right"
        autoClose={5000}
        hideProgressBar={false}
        newestOnTop={false}
        closeOnClick={false}
        rtl={false}
        pauseOnFocusLoss
        draggable
        pauseOnHover
        theme="colored"
        transition={Bounce}
      />
        <div style={{ width: '25%', borderRight: '1px solid #ccc' }}> {/* NodeManager with 25% width */}
          <NodeManager />
        </div>
        <div style={{ width: '75%' }}>
          <ReactFlowProvider>
          <Canvas />
          </ReactFlowProvider>
        </div>
        {session.last_run_status === 'success' && session.selected_node && (
          <DataInspector pipelineName={session.pipeline_name} nodeName={session.selected_node} />
        )}
        {/* <div style={{ marginLeft: '20px' }}>
          <NodeInspector />
          <DataInspector data={{}} /> 
        </div> */}
      </div>
      )}
      </DnDProvider>
  );
};

export default App;
